import React, { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import { PlacesAutocomplete, loadGoogleMapsScript } from "@/components/PlacesAutocomplete";

// Phone number validation - accepts 10 digits, optionally with +91 or 0 prefix
const validatePhone = (phone) => {
  if (!phone || phone.trim() === "") return { valid: false, error: "Phone number is required" };
  const cleaned = phone.replace(/[\s\-\(\)]/g, "");
  // Match: 10 digits, or +91 followed by 10 digits, or 0 followed by 10 digits
  const pattern = /^(\+91|0)?[6-9]\d{9}$/;
  if (!pattern.test(cleaned)) {
    return { valid: false, error: "Enter valid 10-digit mobile number" };
  }
  return { valid: true, error: null };
};

export function DeliveryAddressPicker({
  pickupAddress,
  setPickupAddress,
  pickupContact,
  setPickupContact,
  pickupCoords,
  setPickupCoords,
  dropAddress,
  setDropAddress,
  dropContact,
  setDropContact,
  dropCoords,
  setDropCoords,
  gettingLocation,
  onGetCurrentLocation,
  onValidationChange
}) {
  const [pickupPhoneError, setPickupPhoneError] = useState(null);
  const [dropPhoneError, setDropPhoneError] = useState(null);
  const [pickupTouched, setPickupTouched] = useState(false);
  const [dropTouched, setDropTouched] = useState(false);

  // Validate phones and notify parent
  const validateAndNotify = useCallback(() => {
    const pickupResult = validatePhone(pickupContact);
    const dropResult = validatePhone(dropContact);
    
    if (pickupTouched) setPickupPhoneError(pickupResult.error);
    if (dropTouched) setDropPhoneError(dropResult.error);
    
    if (onValidationChange) {
      onValidationChange({
        pickupPhoneValid: pickupResult.valid,
        dropPhoneValid: dropResult.valid
      });
    }
  }, [pickupContact, dropContact, pickupTouched, dropTouched, onValidationChange]);

  useEffect(() => {
    validateAndNotify();
  }, [validateAndNotify]);
  // Load Google Maps script on mount
  useEffect(() => {
    loadGoogleMapsScript().catch(() => {
      // Script load failed, will use backend fallback
    });
  }, []);

  const handlePickupPhoneChange = (e) => {
    setPickupContact(e.target.value);
    if (!pickupTouched) setPickupTouched(true);
  };

  const handleDropPhoneChange = (e) => {
    setDropContact(e.target.value);
    if (!dropTouched) setDropTouched(true);
  };

  const handlePickupPhoneBlur = () => {
    setPickupTouched(true);
    const result = validatePhone(pickupContact);
    setPickupPhoneError(result.error);
  };

  const handleDropPhoneBlur = () => {
    setDropTouched(true);
    const result = validatePhone(dropContact);
    setDropPhoneError(result.error);
  };

  const handlePickupSelect = (place) => {
    setPickupAddress(place.address);
    if (place.lat && place.lng) {
      setPickupCoords({ lat: place.lat, lng: place.lng });
    }
  };

  const handleDropSelect = (place) => {
    setDropAddress(place.address);
    if (place.lat && place.lng) {
      setDropCoords({ lat: place.lat, lng: place.lng });
    }
  };

  return (
    <div className="bg-card border border-border p-6 space-y-4 overflow-visible">
      {/* Pickup */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-green-500 flex items-center justify-center flex-shrink-0 mt-1">
          <MapPin className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 space-y-2 overflow-visible">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Pickup Location</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onGetCurrentLocation}
              disabled={gettingLocation}
              className="text-xs text-primary hover:text-primary/80 h-auto py-1"
              data-testid="use-current-location-btn"
            >
              {gettingLocation ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Navigation className="w-3 h-3 mr-1" />
              )}
              Use Current Location
            </Button>
          </div>
          <div className="relative z-20">
            <PlacesAutocomplete
              value={pickupAddress}
              onChange={setPickupAddress}
              onSelect={handlePickupSelect}
              placeholder="Search pickup location..."
              testId="pickup-address-input"
            />
            {pickupCoords && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            )}
          </div>
          <Input
            placeholder="Pickup contact number *"
            value={pickupContact}
            onChange={handlePickupPhoneChange}
            onBlur={handlePickupPhoneBlur}
            className={`rounded-none ${pickupPhoneError && pickupTouched ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
            data-testid="pickup-contact-input"
            required
          />
          {pickupPhoneError && pickupTouched && (
            <div className="flex items-center gap-1 text-xs text-red-500 mt-1" data-testid="pickup-phone-error">
              <AlertCircle className="w-3 h-3" />
              {pickupPhoneError}
            </div>
          )}
        </div>
      </div>

      {/* Divider with arrow */}
      <div className="flex items-center gap-3 pl-3">
        <div className="w-0.5 h-6 bg-border ml-3.5" />
      </div>

      {/* Drop */}
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 bg-red-500 flex items-center justify-center flex-shrink-0 mt-1">
          <MapPin className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 space-y-2 overflow-visible">
          <Label className="font-semibold">Drop Location</Label>
          <div className="relative z-10">
            <PlacesAutocomplete
              value={dropAddress}
              onChange={setDropAddress}
              onSelect={handleDropSelect}
              placeholder="Search drop location..."
              testId="drop-address-input"
            />
            {dropCoords && (
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
            )}
          </div>
          <Input
            placeholder="Drop contact number *"
            value={dropContact}
            onChange={handleDropPhoneChange}
            onBlur={handleDropPhoneBlur}
            className={`rounded-none ${dropPhoneError && dropTouched ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
            data-testid="drop-contact-input"
            required
          />
          {dropPhoneError && dropTouched && (
            <div className="flex items-center gap-1 text-xs text-red-500 mt-1" data-testid="drop-phone-error">
              <AlertCircle className="w-3 h-3" />
              {dropPhoneError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
